import { AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { SessionStorageKeys } from "common/enums";
import axios from "axios";
import { translate } from "common/utils";
import UserStore from "client/Data/UserStore";
import { HttpClient } from "./http-client";
import { apiPaths } from "./paths";

const SSO_PENDING_REQUESTS_KEY = "sso_pending_requests";
const SSO_SKIPPED_UPLOADS_KEY = "sso_skipped_uploads";

// Serializable version of request config (without functions)
type SerializableRequestConfig = {
  url?: string;
  method?: Method;
  data?: any;
  params?: any;
  headers?: Record<string, string>;
};

// Result of restoring requests after SSO
type RestoreResult = {
  restoredCount: number;
  skippedUploadsCount: number;
};

/**
 * Checks if request data contains non-serializable content like FormData, File, or Blob.
 * These cannot be stored in sessionStorage and will be lost during SSO redirect.
 */
function isNonSerializableData(data: unknown): boolean {
  if (!data) return false;

  const nonSerializableTypes = [FormData, File, Blob, ArrayBuffer];
  return nonSerializableTypes.some(type => typeof type !== "undefined" && data instanceof type);
}

// ESM live binding — `UserStore` is captured by reference, so the cycle
// (api.ts → UserStore → api.ts → session-recovery → UserStore) resolves at
// runtime when `getUserStore()` is actually called, after both modules have
// fully evaluated. Replaces a Webpack-only `require()` lazy hack that broke
// the Vite/Rollup browser build with "require is not defined".
const getUserStore = () => UserStore;

type QueuedRequest = {
  config: AxiosRequestConfig;
  resolve: (value: AxiosResponse) => void;
  reject: (error: any) => void;
};

class SessionRecoveryService {
  private failedRequestQueue: QueuedRequest[] = [];
  private isRecovering = false;
  private interceptorId: number | null = null;

  setupInterceptor() {
    if (this.interceptorId !== null) return;

    this.interceptorId = axios.interceptors.response.use(
      response => response,
      error => {
        const status = error.response?.status;
        const isLoginRequest = error.config?.url?.includes(apiPaths.login_path);
        const isRetry = error.config?.__isRetry;
        const UserStore = getUserStore();

        // Don't intercept login requests, retried requests, non-auth errors, or when user is not logged in
        if (
          isLoginRequest ||
          isRetry ||
          (status !== 401 && status !== 419) ||
          !UserStore.isUserLoggedIn
        ) {
          return Promise.reject(error);
        }

        // Only trigger the session expiry modal if not already showing
        // This prevents multiple modals when multiple API calls fail simultaneously
        if (!UserStore.isSessionExpired) {
          const message =
            status === 419
              ? translate("apiMessages:logged-out-different-device")
              : translate("apiMessages:session-expired");
          UserStore.setSessionExpired(true, message);
        }

        // Queue the failed request and return a promise that resolves after retry
        return this.queueFailedRequest(error.config);
      }
    );
  }

  queueFailedRequest(config: AxiosRequestConfig): Promise<AxiosResponse> {
    return new Promise((resolve, reject) => {
      this.failedRequestQueue.push({ config, resolve, reject });
    });
  }

  isRecoveryInProgress(): boolean {
    return this.isRecovering;
  }

  async reAuthenticate(password: string): Promise<void> {
    const UserStore = getUserStore();
    const username =
      UserStore.currentUser?.username || sessionStorage.getItem(SessionStorageKeys.USER_ID);

    if (!username) {
      throw new Error("No username available for re-authentication");
    }

    this.isRecovering = true;

    try {
      const res = await HttpClient.post(apiPaths.login_path, {
        username,
        password
      });

      sessionStorage.setItem(SessionStorageKeys.TOKEN, res.data.token);
      sessionStorage.setItem(SessionStorageKeys.LANGUAGE, res.data.user.language || "en");
      axios.defaults.headers.common["token"] = res.data.token;

      // Notify other tabs of session update
      window.dispatchEvent(new Event("storage"));

      getUserStore().clearSessionExpired();
      await this.retryQueuedRequests();

      this.isRecovering = false;
    } catch (error: any) {
      this.isRecovering = false;

      // Don't cancel queue here - user can retry with correct password
      // Queue is only cancelled when user explicitly cancels the modal

      const errorMessage =
        error.response?.data?.error?.message || error.message || "Re-authentication failed";

      throw new Error(errorMessage);
    }
  }

  private async retryQueuedRequests(): Promise<void> {
    const queue = [...this.failedRequestQueue];
    this.failedRequestQueue = [];

    for (const { config, resolve, reject } of queue) {
      try {
        // Update the token and mark as retry to prevent re-interception
        const newConfig = {
          ...config,
          __isRetry: true,
          headers: {
            ...config.headers,
            token: sessionStorage.getItem(SessionStorageKeys.TOKEN)
          }
        };

        const response = await axios.request(newConfig);
        resolve(response);
      } catch (error) {
        reject(error);
      }
    }
  }

  private rejectAllQueued(reason: string) {
    const queue = [...this.failedRequestQueue];
    this.failedRequestQueue = [];
    const err = new Error(reason);
    (err as any).isSessionCancelled = true;
    for (const { reject } of queue) {
      reject(err);
    }
  }

  cancelQueuedRequests() {
    this.rejectAllQueued("Session recovery cancelled");
  }

  handleSSORelogin(ssoProvider: string) {
    // Save pending requests to sessionStorage before redirect (instead of cancelling)
    this.saveQueuedRequestsToStorage();

    const UserStore = getUserStore();
    const username =
      UserStore.currentUser?.username ||
      UserStore.currentUser?.email ||
      sessionStorage.getItem(SessionStorageKeys.USER_ID) ||
      "";
    // Redirect to SSO login endpoint (same as login page does)
    window.location.href = `${window.location.origin}/api/v2/auth/sso/${encodeURIComponent(ssoProvider)}/login?u=${encodeURIComponent(username)}`;
  }

  /**
   * Saves the pending request queue to sessionStorage before SSO redirect.
   * This preserves API requests that failed due to session expiry so they
   * can be retried after the user re-authenticates via SSO.
   *
   * Note: File uploads (FormData, File, Blob) cannot be serialized and will be skipped.
   * The count of skipped uploads is stored separately to warn the user.
   */
  private saveQueuedRequestsToStorage() {
    if (this.failedRequestQueue.length === 0) return;

    try {
      const requestConfigs: SerializableRequestConfig[] = [];
      let skippedUploadsCount = 0;

      for (const req of this.failedRequestQueue) {
        // Skip requests with non-serializable data (file uploads)
        if (isNonSerializableData(req.config.data)) {
          skippedUploadsCount++;
          console.warn(
            "Skipping file upload request - cannot be preserved across SSO redirect:",
            req.config.url
          );
          continue;
        }

        // Filter out token header (will be updated after re-auth)
        const filteredHeaders = req.config.headers
          ? (Object.fromEntries(
              Object.entries(req.config.headers).filter(
                ([key, value]) => key.toLowerCase() !== "token" && typeof value === "string"
              )
            ) as Record<string, string>)
          : undefined;

        requestConfigs.push({
          url: req.config.url,
          method: req.config.method,
          data: req.config.data,
          params: req.config.params,
          headers: filteredHeaders
        });
      }

      sessionStorage.setItem(SSO_PENDING_REQUESTS_KEY, JSON.stringify(requestConfigs));

      // Store the count of skipped uploads to warn user after SSO login
      if (skippedUploadsCount > 0) {
        sessionStorage.setItem(SSO_SKIPPED_UPLOADS_KEY, String(skippedUploadsCount));
      }

      // Settle all queued promises - SSO redirect will abandon them anyway,
      // but this prevents memory leaks from unsettled promises
      this.rejectAllQueued("Redirecting to SSO login");
    } catch (error) {
      console.error("Failed to save pending requests to storage:", error);
      this.rejectAllQueued("Failed to save pending requests");
    }
  }

  /**
   * Restores and retries pending requests from sessionStorage after SSO login.
   * Call this after detecting a successful SSO callback.
   * @returns RestoreResult with counts of restored and skipped requests, or null if nothing to restore
   */
  async restoreAndRetryQueuedRequests(): Promise<RestoreResult | null> {
    const savedRequests = sessionStorage.getItem(SSO_PENDING_REQUESTS_KEY);
    const skippedUploadsStr = sessionStorage.getItem(SSO_SKIPPED_UPLOADS_KEY);

    // Clean up storage immediately to prevent duplicate retries
    sessionStorage.removeItem(SSO_PENDING_REQUESTS_KEY);
    sessionStorage.removeItem(SSO_SKIPPED_UPLOADS_KEY);

    const skippedUploadsCount = skippedUploadsStr ? parseInt(skippedUploadsStr, 10) : 0;

    if (!savedRequests && skippedUploadsCount === 0) {
      return null;
    }

    try {
      const requestConfigs: SerializableRequestConfig[] = savedRequests
        ? JSON.parse(savedRequests)
        : [];
      let restoredCount = 0;

      if (requestConfigs.length > 0) {
        console.log(
          `Restoring ${requestConfigs.length} pending request(s) after SSO re-authentication`
        );

        // Retry each request with the new auth token
        for (const config of requestConfigs) {
          try {
            await axios.request({
              url: config.url,
              method: config.method,
              data: config.data,
              params: config.params,
              headers: {
                ...config.headers,
                token: sessionStorage.getItem(SessionStorageKeys.TOKEN) || ""
              }
            });
            restoredCount++;
          } catch (error) {
            // Log but don't throw - continue with other requests
            console.error("Failed to retry request after SSO:", config.url, error);
          }
        }
      }

      return { restoredCount, skippedUploadsCount };
    } catch (error) {
      console.error("Failed to restore pending requests:", error);
      return { restoredCount: 0, skippedUploadsCount };
    }
  }

  /**
   * Checks if there are pending requests saved in sessionStorage.
   */
  hasPendingStoredRequests(): boolean {
    const savedRequests = sessionStorage.getItem(SSO_PENDING_REQUESTS_KEY);
    if (!savedRequests) return false;

    try {
      const parsed = JSON.parse(savedRequests);
      return Array.isArray(parsed) && parsed.length > 0;
    } catch {
      return false;
    }
  }

  handleFullLogout() {
    const UserStore = getUserStore();
    this.cancelQueuedRequests();
    UserStore.clearSessionExpired();
    UserStore.isUserLoggedIn = false;
    sessionStorage.clear();
    // Clear axios token header to prevent stale token from triggering 401s
    delete axios.defaults.headers.common["token"];
  }

  getQueueLength(): number {
    return this.failedRequestQueue.length;
  }
}

export const sessionRecoveryService = new SessionRecoveryService();

// Initialize the interceptor
sessionRecoveryService.setupInterceptor();
